import React, { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Camera, RotateCw, CheckCircle2, ScanFace, Eye, Smile, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * BiometricCapture
 * Lightweight selfie + liveness pass: user proceeds through a sequence of
 * prompts ("look straight", "blink", "smile") and one frame is captured at
 * each step. The first frame becomes the `photo`. Other frames are returned
 * as liveness frames.
 *
 * onComplete({ photo, livenessFrames, livenessPassed })
 *   photo: { name, content_type, data, size }
 *   livenessFrames: array of { name, content_type, data, size }
 *   livenessPassed: boolean
 */
const PROMPTS = [
  { id: "straight", label: "Look straight at the camera", Icon: ScanFace, hold: 1200 },
  { id: "blink", label: "Now blink your eyes", Icon: Eye, hold: 1500 },
  { id: "smile", label: "Great. Now smile!", Icon: Smile, hold: 1500 },
];

export default function BiometricCapture({ onComplete, onCancel, testId = "biometric-capture" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [step, setStep] = useState(0);
  const [capturing, setCapturing] = useState(false);
  const [frames, setFrames] = useState([]); // { id, data, size, content_type, name }
  const [cameraReady, setCameraReady] = useState(false);
  const [error, setError] = useState("");

  const startCamera = useCallback(async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraReady(true);
      }
    } catch (e) {
      console.error(e);
      setError("Camera access denied or unavailable. You can still upload a photo file as a fallback.");
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [startCamera]);

  const captureFrame = (id) => {
    const v = videoRef.current;
    if (!v) return null;
    const w = v.videoWidth || 480;
    const h = v.videoHeight || 480;
    const size = Math.min(w, h);
    const canvas = document.createElement("canvas");
    canvas.width = 640;
    canvas.height = 640;
    const ctx = canvas.getContext("2d");
    // Center-crop square + mirror (selfie style)
    const sx = (w - size) / 2;
    const sy = (h - size) / 2;
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(v, sx, sy, size, size, 0, 0, canvas.width, canvas.height);
    ctx.restore();
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    const base64 = dataUrl.split(",")[1] || "";
    const approxBytes = Math.floor(base64.length * 0.75);
    return {
      id,
      name: `liveness-${id}.jpg`,
      content_type: "image/jpeg",
      data: base64,
      size: approxBytes,
    };
  };

  const advance = async () => {
    if (!cameraReady) return;
    setCapturing(true);
    const prompt = PROMPTS[step];
    // Allow the user a moment to comply with the prompt before snapping.
    await new Promise((r) => setTimeout(r, prompt.hold));
    const frame = captureFrame(prompt.id);
    if (!frame) { setCapturing(false); return; }
    const next = [...frames, frame];
    setFrames(next);
    setCapturing(false);
    if (step + 1 < PROMPTS.length) {
      setStep(step + 1);
    } else {
      // Done — first frame becomes the official photo.
      const [photo, ...rest] = next;
      const finalPhoto = { ...photo, name: "selfie.jpg" };
      onComplete({ photo: finalPhoto, livenessFrames: rest, livenessPassed: true });
      // Stop camera
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    }
  };

  const retake = () => {
    setStep(0);
    setFrames([]);
  };

  const Prompt = PROMPTS[step];
  const Icon = Prompt.Icon;

  return (
    <div className="fv-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-eyebrow text-[#4F4D45]">Liveness check</div>
          <div className="font-display text-xl font-medium mt-1">{Prompt.label}</div>
        </div>
        <div className="text-xs text-[#4F4D45]" data-testid={`${testId}-step`}>{step + 1} / {PROMPTS.length}</div>
      </div>

      <div className="mt-4 relative rounded-2xl overflow-hidden bg-[#0A0A08] aspect-square max-w-sm mx-auto" data-testid={`${testId}-stage`}>
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white/90 text-sm" data-testid={`${testId}-error`}>
            {error}
          </div>
        ) : (
          <video
            ref={videoRef}
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: "scaleX(-1)" }}
            data-testid={`${testId}-video`}
          />
        )}
        {/* Oval mask hint */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="w-[70%] h-[88%] rounded-[50%] border-2 border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
        </div>
        {/* Prompt icon */}
        <div className="absolute top-3 left-3 right-3 flex items-center gap-2">
          <div className="bg-white/15 backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2">
            <Icon className="w-3.5 h-3.5" strokeWidth={2} /> {Prompt.label}
          </div>
        </div>
        {capturing && (
          <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px] flex items-center justify-center" data-testid={`${testId}-flash`}>
            <div className="w-14 h-14 rounded-full bg-white/90 text-[#1B3526] flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          </div>
        )}
      </div>

      {/* Captured thumbnails */}
      {frames.length > 0 && (
        <div className="mt-4 flex items-center gap-2 justify-center" data-testid={`${testId}-thumbs`}>
          {frames.map((f, i) => (
            <div key={i} className="w-12 h-12 rounded-lg overflow-hidden border border-[#D2CDBE] relative">
              <img src={`data:${f.content_type};base64,${f.data}`} alt={f.id} className="w-full h-full object-cover" />
              <CheckCircle2 className="absolute -bottom-1 -right-1 w-4 h-4 text-[#264C39] bg-white rounded-full" />
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 flex items-center gap-3 justify-between">
        <Button type="button" variant="ghost" onClick={onCancel} data-testid={`${testId}-cancel`}>Use photo upload instead</Button>
        <div className="flex items-center gap-2">
          {frames.length > 0 && (
            <Button type="button" variant="outline" onClick={retake} data-testid={`${testId}-retake`} className="border-[#D2CDBE]">
              <RotateCw className="w-4 h-4 mr-2" /> Retake
            </Button>
          )}
          <Button
            type="button"
            onClick={advance}
            disabled={!cameraReady || capturing || !!error}
            className="bg-[#1B3526] hover:bg-[#0F2517]"
            data-testid={`${testId}-capture`}
          >
            {capturing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-2" /> Capture</>}
          </Button>
        </div>
      </div>
      <p className="text-xs text-[#4F4D45] mt-3 text-center">We capture three frames during simple liveness prompts. Frames stay on your account for review.</p>
    </div>
  );
}
